import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'

import DateTimeDisplay from '../../components/DateTimeDisplay'
import DetailPageTemplate from '../../templates/DetailPageTemplate'
import fetchWithAuth from '../../utilities/fetchWithAuth'

const OrganizationPage = () => {
  const [organization, setOrganization] = useState(null)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const { uuid } = useParams()
  const navigate = useNavigate()

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await fetchWithAuth(`organizations/${uuid}`)
        setOrganization(data)
      } catch (error) {
        console.error('Error fetching organization:', error)
      }
    }

    fetchData()
  }, [uuid])

  const handleCloseDialog = () => {
    setOpenDeleteDialog(false)
  }

  const handleDelete = async () => {
    try {
      await fetchWithAuth(`organizations/${uuid}`, { method: 'DELETE' })
      navigate('/organizations')
    } catch (error) {
      console.error('Error deleting organization:', error)
    }
  }

  if (!organization) {
    return <DetailPageTemplate isLoading={true} />
  }

  const detailsSection = {
    title: 'Organization Details',
    content: (
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={3}>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ width: 100 }}
              >
                Name
              </Typography>
              <Typography>{organization.name}</Typography>
            </Box>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ width: 100 }}
              >
                Address
              </Typography>
              <Typography>{organization.address}</Typography>
            </Box>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ width: 100 }}
              >
                Start Date
              </Typography>
              <DateTimeDisplay date={new Date(organization.startDate)} />
            </Box>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ width: 100 }}
              >
                Phone
              </Typography>
              <Typography>{organization.phone}</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ width: 100 }}
              >
                Status
              </Typography>
              <Chip
                label={organization.active ? 'Active' : 'Inactive'}
                color={organization.active ? 'success' : 'default'}
                size="small"
              />
            </Box>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ width: 100 }}
              >
                Created At
              </Typography>
              <DateTimeDisplay date={new Date(organization.createdAt)} />
            </Box>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ width: 100 }}
              >
                Updated At
              </Typography>
              <DateTimeDisplay date={new Date(organization.updatedAt)} />
            </Box>
          </Stack>
        </CardContent>
      </Card>
    ),
  }

  return (
    <>
      <DetailPageTemplate
        title={organization.name}
        breadcrumbs={['organizations', organization.name]}
        actions={
          <Stack direction="row" spacing={2}>
            <Button
              component={Link}
              to={`/organizations/${uuid}/edit`}
              variant="contained"
              color="primary"
            >
              Edit Organization
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setOpenDeleteDialog(true)}
            >
              Delete Organization
            </Button>
          </Stack>
        }
        sections={[detailsSection]}
      />

      <Dialog open={openDeleteDialog} onClose={handleCloseDialog}>
        <DialogTitle>Delete Organization</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {organization.name}? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default OrganizationPage
